// hooks/useAuth.js
import { useState, useEffect, useCallback } from 'react';
import { authenticate } from '../auth';
import { setCookie, getCookie, removeCookie } from '../utils/cookies';
import { saveLocale } from '../utils/locale';
import { saveDesignVersion } from '../utils/designVersion';
import { saveTimezone } from '../utils/timezone';
import { perfMark } from '../utils/perfTracker';

function isTelegramWebApp() {
    return Boolean(window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData);
}

const TOKEN_COOKIE_KEY = 'jwt_token';

const useAuth = () => {
    const [token, setToken] = useState(null);
    const [error, setError] = useState(null); // Состояние для ошибок

    const initAuth = useCallback(async () => {
        if (isTelegramWebApp()) {
            const initData = window.Telegram.WebApp.initData || '';
            try {
                const authData = await authenticate(initData);
                setToken(authData.token);
                perfMark('authComplete');
                // Save locale on authentication
                if (authData.locale) {
                    saveLocale(authData.locale);
                }
                // Save design version on authentication
                if (authData.designVersion) {
                    saveDesignVersion(authData.designVersion);
                }
                // Save timezone on authentication (including empty marker if not set)
                saveTimezone(authData.timezone);
                window.Telegram.WebApp.ready();
            } catch (error) {
             //   console.error('Authentication failed:', error);
                if (error.response && error.response.status === 401 && error.response.data.result === 'not registered') {
                    setError({
                        title: error.response.data.title || 'Almost done',
                        description: error.response.data.description || 'Complete registration in the game bot using the /start command and select a castle.',
                    });
                } else {
                    // If auth error (401) and not Telegram WebApp - remove token from cookies
                    if (!isTelegramWebApp() && error.response && error.response.status === 401) {
                        removeCookie(TOKEN_COOKIE_KEY);
                    }
                    setError({
                        title: 'Auth Error',
                        description: error.response?.data?.message || 'Please try again later',
                    });
                }
            }
        } else {
            // Try to get token from cookie first
            const cookieToken = getCookie(TOKEN_COOKIE_KEY);
            if (cookieToken) {
                setToken(cookieToken);
                perfMark('authComplete');
                return;
            }
            // No token in cookie, do not call authenticate, just show WebAuthForm
            setToken(null);
        }
    }, []);

    useEffect(() => {
        initAuth();
    }, [initAuth]);

    // Function to update token (e.g., after WebAuthForm)
    const updateToken = (authData) => {
        if (typeof authData === 'string') {
            // Backward compatibility - if only token is passed
            setToken(authData);
            if (!isTelegramWebApp()) {
                setCookie(TOKEN_COOKIE_KEY, authData);
            }
        } else {
            // New format - object with token and locale
            setToken(authData.token);
            // Save locale on authentication
            if (authData.locale) {
                saveLocale(authData.locale);
            }
            // Save design version on authentication
            if (authData.designVersion) {
                saveDesignVersion(authData.designVersion);
            }
            // Save timezone on authentication (including empty marker if not set)
            saveTimezone(authData.timezone);
            if (!isTelegramWebApp()) {
                setCookie(TOKEN_COOKIE_KEY, authData.token);
            }
        }
    };

    return { token, error, updateToken, isTelegram: isTelegramWebApp() };
};

export default useAuth;